import React, {useEffect, useState} from 'react';
import {Box, Dialog, Grid, Input, Link, Typography} from '@mui/material';
import styles from "./Login.module.css";
import {useAuth} from "../../services/AuthProvider";
import {resendConfirmationCode} from "../../services/HttpService";
import {useLocation, useNavigate} from "react-router-dom";
import backgroundImage from "../../resources/Login/login-image-trimmed.png";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import SnackbarAlertMessage from "../../components/Modal/SnackbarAlertMessage";
import SignUpBannerFooter from "../../components/Footer/SignUpBannerFooter";
import {UsernamePattern} from "../../services/Util";
import SpinnerModal from "../../components/Modal/Spinner";
import GreenTickSVG from "../../resources/chevron/green-tick.svg";

const Login: React.FC = () => {
  const {isLoggedIn, login}= useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [openReLoginAlertMessage, setReLoginOpenAlertMessage] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [openMigrationDialog, setOpenMigrationDialog] = useState(false);
  const [showResetPasswordSuccess, setShowResetPasswordSuccess] = useState(-1); // init -1; show 1; hide 0

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/");
    } else {
      // re-login? show alert message.
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('re') === '1') {
        setReLoginOpenAlertMessage(true);
      }
      const usernameParam = searchParams.get('username');
      if (usernameParam && (UsernamePattern.test(decodeURIComponent(usernameParam) ))) {
        setUsername(decodeURIComponent(usernameParam));
      }
      if (searchParams.get('reset_password') && showResetPasswordSuccess === -1) {
        setShowResetPasswordSuccess(1);
        setIsBannerOpen(true);
      }
    }
  }, [isLoggedIn, navigate, location, username, showResetPasswordSuccess]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setShowSpinner(true);
      await login(username, password);
      setShowSpinner(false);
    } catch (error: any) {
      setShowSpinner(false);
      if (error.message.indexOf("403") > 0 && error.message.indexOf("User is not confirmed") > 0) {
        // resend the activate link
        await resendConfirmationCode(username);
        navigate(`/activate?username=${encodeURIComponent(username)}`);
      } else if (error.message.indexOf("422") > 0 && error.message.indexOf("Please reset the password") > 0) {
        setOpenMigrationDialog(true);
      } else {
        setShowResetPasswordSuccess(0);
        setIsBannerOpen(true);
      }
    }
  };

  return (
    <div className={styles.Login} data-testid="login">
      <SpinnerModal open={showSpinner} />

      <SnackbarAlertMessage
        open={openReLoginAlertMessage}
        severity={"info"}
        message={"Authentication required, please login again"}
        onClose={() => setReLoginOpenAlertMessage(false)}
      />

      <Dialog
        open={openMigrationDialog}
        onClose={() => setOpenMigrationDialog(false)}
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '630px' },
            maxWidth: '100%',
            borderRadius: '15.689px',
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 3, sm: 8 },
            py: { xs: 3, sm: 5 },
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontSize: { xs: '26px', sm: '34px' } }}
          >
            Please reset your password
          </Typography>

          <Typography
            variant="body1"
            sx={{ py: { xs: '16px', sm: '18px' } }}
          >
            SPARX has been updated to make your experience even better!
            <br />
            <br />
            To keep your account secure, you’ll need to <b>reset your password before logging in</b> and follow the steps.
          </Typography>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'space-evenly',
              alignItems: 'center',
            }}
          >
            <button
              className={`PrimaryButton WhiteButton ${styles.BlueButtonBorder}`}
              style={{ width: '200px' }}
              onClick={() => setOpenMigrationDialog(false)}
            >
              Cancel
            </button>
            <button
              className="PrimaryButton"
              style={{ width: '200px' }}
              onClick={() => {
                setOpenMigrationDialog(false);
                navigate('/forgot-password');
              }}
            >
              Reset Password
            </button>
          </Box>
        </Box>
      </Dialog>

      <section id="LoginTitle" className={styles.LoginTitle}>
        <Typography variant="h1">Account Login</Typography>
      </section>

      <div style={{width: '100%'}}>
        {isBannerOpen && <div className={showResetPasswordSuccess ? styles.LoginBannerSuccess : styles.LoginBanner}>
          {
            showResetPasswordSuccess ?
              <div style={{display: "flex", alignItems: "center"}}>
                <img src={GreenTickSVG} alt="tick" style={{marginRight: 20}} />
                <div>Your password has been successfully updated.</div>
              </div>
              :
              <div>The username or password is incorrect. Please try again. If you are still having issues, please <a href="https://www.sparx.org.nz/contact">contact us</a>.</div>
          }
          <CloseIcon onClick={() => {setIsBannerOpen(false); setShowResetPasswordSuccess(0)}}/>
        </div>
        }
      </div>

      <Grid container>
        {/* Left Side - Form */}
        <Grid item xs={12} md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 0'
          }}
        >
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '80%', maxWidth: 500 }}>
            <div className="ShowFor768BigScreenOnly">
              <Typography variant="h3" gutterBottom>Login in to SPARX</Typography>
              <br/><br/>
            </div>

            {/* Username Field */}
            <Box sx={{ marginBottom: 5 }}>
              <Typography variant="subtitle2" gutterBottom>Username</Typography>
              <Input fullWidth disableUnderline placeholder="Enter your username" value={username} onChange={(e) => setUsername(e.target.value)}/>
            </Box>
            {/* Password Field */}
            <Box sx={{ marginBottom: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>Password</Typography>
                <Typography variant="body1" align="right">
                  <Link href="/forgot-password" sx={{ textDecoration: 'none', color: '#2D94E8' }} tabIndex={-1}>
                    Forgot your password?
                  </Link>
                </Typography>
              </Box>
              <Input fullWidth disableUnderline placeholder="Enter your password"
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     type={showPassword ? 'text' : 'password'}
                     endAdornment={
                       <InputAdornment position="end">
                         <IconButton
                           aria-label="toggle password visibility"
                           onClick={(e) => {e.preventDefault(); setShowPassword((show) => !show);}}
                           edge="end"
                           tabIndex={-1}
                         >
                           {showPassword ? <Visibility /> : <VisibilityOff />}
                         </IconButton>
                       </InputAdornment>
                     }
              />
            </Box>

            <button className={`PrimaryButton ${styles.LoginButton}`}>Login</button>

            <Typography align="left">Don't have an account?{' '}
              <Link href="/register" sx={{ textDecoration: 'none', color: '#2D94E8' }}>Sign up</Link>
            </Typography>
          </Box>
        </Grid>

        {/* Right Side - Image */}
        <Grid item xs={false} md={6} className="ShowFor768BigScreenOnly"
          sx={{
            minHeight: '800px',
            marginBottom: '20px',
            backgroundImage: `url(${backgroundImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPositionX: 'right'
          }}
        />
      </Grid>

      <SignUpBannerFooter />
    </div>
  );
};

export default Login;
